import { useOutstandingInvoices } from 'modules/Insights/widgets/OutstandingInvoices/useOutstandingInvoices'
import {
  Text,
  GreyGrey30,
  GreyGrey40,
  GreyGrey50,
  GreyGrey60,
  GreyGrey90,
  IndigoIndigo50,
  Link,
  Primary12Regular,
  Primary13Regular,
  Primary14SemiBold,
  Primary20SemiBold,
  InfoPopover,
  Skeleton,
  IconCircleInfo,
  IconWallet,
  Box,
  Flex,
  Grid
} from '@sequencehq/core-components'
import { OutstandingInvoicesGraph } from 'modules/Insights/widgets/OutstandingInvoices/OutstandingInvoicesGraph'
import { EmptyState } from 'modules/Insights/common/EmptyState'
import { sub } from '@sequencehq/utils/dates'
import { Currency } from '@sequencehq/api/utils/commonEnums'
import { OutstandingInvoicesItem } from 'modules/Insights/widgets/OutstandingInvoices/OutstandingInvoicesItem'

const InfoTooltip = () => {
  return (
    <InfoPopover
      variant="info"
      minimal
      padding="0"
      placement="top"
      body={
        <Box width={300}>
          The AR aging summary categorizes your receivables by the amount of
          time an invoice has been outstanding, giving you a view of your
          revenue collections health.{' '}
          <Link
            to="https://docs.sequencehq.com/invoicing/invoicing-customers#payment-status"
            isExternal
            {...Primary14SemiBold}
            color="indigo.50"
          >
            Learn more
          </Link>
        </Box>
      }
      icon={<IconCircleInfo color={GreyGrey50} />}
    />
  )
}

const getLinks = (currency?: Currency | null) => {
  if (!currency) {
    return {}
  }

  const basePath = `/invoices?sortBy=accountingDate_desc&invoicePaymentStatus=UNPAID&invoiceStatus=SENT&invoiceCurrency=${currency}`

  const NOW = new Date()

  return {
    overdueFrom61days: `${basePath}&dueBefore=${sub(NOW, { days: 61 })}`,
    overdueFrom31to60days: `${basePath}&dueBefore=${sub(NOW, {
      days: 31
    })}&dueAfter=${sub(NOW, { days: 60 })}`,
    overdueFrom0to30days: `${basePath}&dueBefore=${NOW}&dueAfter=${sub(NOW, {
      days: 30
    })}`,
    outstandingNow: `${basePath}&dueAfter=${NOW}`
  }
}

interface OverviewArAgingProps {
  currency?: Currency | null
}

export const OverviewArAging = ({ currency }: OverviewArAgingProps) => {
  const { data, isLoading, valueFormat } = useOutstandingInvoices({
    currency
  })

  const links = getLinks(currency)

  if (isLoading || !data) {
    return <Skeleton width="400px" height="280px" borderRadius="8px" />
  }

  return (
    <Box
      width="400px"
      height="280px"
      boxShadow="0 2px 4px 0 rgba(15, 23, 42, 0.08)"
      border={`1px solid ${GreyGrey40}`}
      backgroundColor="white"
      borderRadius="lg"
      p={4}
      overflow="auto"
    >
      <Flex gap={2} mb={4}>
        <Flex flexGrow="1" flexDirection="column" gap={2}>
          <Text
            as={Flex}
            {...Primary13Regular}
            color={GreyGrey60}
            gap={1}
            fontWeight="semibold"
          >
            AR aging summary
            <InfoTooltip />
          </Text>
          <Text {...Primary20SemiBold} color={GreyGrey90}>
            {valueFormat(data.totalAmount)}
          </Text>
        </Flex>
      </Flex>

      {data.totalAmount === 0 ? (
        <Box mt={2} h="full">
          <EmptyState label="No outstanding invoices" Icon={<IconWallet />} />
        </Box>
      ) : (
        <>
          <OutstandingInvoicesGraph data={data} />
          <Box mt="20px">
            <Grid
              templateColumns="repeat(2, 1fr)"
              pb={2}
              borderBottom={`1px solid ${GreyGrey30}`}
            >
              <Text
                {...Primary12Regular}
                fontWeight="semibold"
                color={GreyGrey60}
              >
                Invoice Age
              </Text>
              <Grid templateColumns="repeat(2, 1fr)">
                <Text
                  {...Primary12Regular}
                  fontWeight="semibold"
                  color={GreyGrey60}
                  pl={6}
                >
                  Invoices
                </Text>
                <Text
                  {...Primary12Regular}
                  fontWeight="semibold"
                  color={GreyGrey60}
                  textAlign="right"
                >
                  Amount
                </Text>
              </Grid>
            </Grid>

            {links.overdueFrom61days && (
              <OutstandingInvoicesItem
                to={links.overdueFrom61days}
                firstColumnText="More than 60 days"
                secondColumnText={`${data.overdueFrom61days.numberOfInvoices}x`}
                thirdColumnText={valueFormat(data.overdueFrom61days.amount)}
                indicatorIconColor={IndigoIndigo50}
              />
            )}
            {links.overdueFrom31to60days && (
              <OutstandingInvoicesItem
                to={links.overdueFrom31to60days}
                firstColumnText="30-60 days overdue"
                secondColumnText={`${data.overdueFrom31to60days.numberOfInvoices}x`}
                thirdColumnText={valueFormat(data.overdueFrom31to60days.amount)}
                indicatorIconColor="#6B73FF"
              />
            )}
            {links.overdueFrom0to30days && (
              <OutstandingInvoicesItem
                to={links.overdueFrom0to30days}
                firstColumnText="0-30 days overdue"
                secondColumnText={`${data.overdueFrom0to30days.numberOfInvoices}x`}
                thirdColumnText={valueFormat(data.overdueFrom0to30days.amount)}
                indicatorIconColor="#9FA6FF"
              />
            )}
            {links.outstandingNow && (
              <OutstandingInvoicesItem
                to={links.outstandingNow}
                firstColumnText="Current (not overdue)"
                secondColumnText={`${data.outstandingNow.numberOfInvoices}x`}
                thirdColumnText={valueFormat(data.outstandingNow.amount)}
                indicatorIconColor={GreyGrey40}
              />
            )}
          </Box>
        </>
      )}
    </Box>
  )
}
